// CSV validation and sanitization utilities

export interface ValidationResult {
  valid: boolean
  error?: string
}

export interface RowValidationResult {
  errors: string[]
  warnings: string[]
}

export interface ProcessedRow {
  first_name: string
  last_name: string
  email: string
  phone_number: string | null
  dob: string | null
  contract_start_date: string | null
  contract_end_date: string | null
  subscription_price: string | null
  subscription_name: string | null
  family_id: string | null
  is_family_lead: boolean | null
  original_start_date: string | null
  subscription_id: string | null
  discount_code_id: string | null
  primary_location_id: string | null
  punch_card_total: number | null
  punch_card_limit: number | null
  punch_card_remaining: number | null
  is_trial_member: boolean | null
  notes: string | null
}

export interface InvalidEmailRecord {
  row: number
  first_name: string | null
  last_name: string | null
  email: string | null
  reason: string
  isDuplicate?: boolean // Flag to distinguish duplicates from invalid/missing emails
}

export interface CSVProcessingResult {
  success: boolean
  validRows: ProcessedRow[]
  errors: string[]
  warnings: string[]
  invalidEmails: InvalidEmailRecord[]
  totalRows: number
  validRowCount: number
}

// Validation functions
export const validateEmail = (email: string): ValidationResult => {
  if (!email) return { valid: false, error: 'Email is required' }
  
  const trimmed = email.trim()
  if (!trimmed) return { valid: false, error: 'Email cannot be empty' }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(trimmed)) {
    return { valid: false, error: 'Invalid email format' }
  }
  
  if (trimmed.length > 254) {
    return { valid: false, error: 'Email address too long' }
  }
  
  return { valid: true }
}

export const validateName = (name: string, fieldName: string): ValidationResult => {
  if (!name) return { valid: false, error: `${fieldName} is required` }
  
  const trimmed = name.trim()
  if (!trimmed) return { valid: false, error: `${fieldName} cannot be empty` }
  
  if (trimmed.length > 100) {
    return { valid: false, error: `${fieldName} must be less than 100 characters` }
  }
  
  // Check for potentially dangerous characters
  const dangerousChars = /[<>'"&]/
  if (dangerousChars.test(trimmed)) {
    return { valid: false, error: `${fieldName} contains invalid characters` }
  }
  
  return { valid: true }
}

export const validatePhoneNumber = (phone: string): ValidationResult => {
  if (!phone) return { valid: true } // Phone is optional
  
  const trimmed = phone.trim()
  if (!trimmed) return { valid: true }
  
  // Remove common formatting characters
  const cleaned = trimmed.replace(/[\s\-().]/g, '')
  
  // Basic phone validation - should be digits possibly with leading +
  const phoneRegex = /^[+]?[1-9][\d]{0,15}$/
  if (!phoneRegex.test(cleaned)) {
    return { valid: false, error: 'Invalid phone number format' }
  }
  
  return { valid: true }
}

export const validateDate = (dateStr: string, fieldName: string): ValidationResult => {
  if (!dateStr) return { valid: true } // Dates are optional
  
  const trimmed = dateStr.trim()
  if (!trimmed) return { valid: true }
  
  // Try to parse MM-DD-YYYY format first (US format)
  const mmddyyyyRegex = /^(\d{1,2})-(\d{1,2})-(\d{4})$/
  const mmddyyyyMatch = trimmed.match(mmddyyyyRegex)
  
  if (mmddyyyyMatch) {
    const [, month, day, year] = mmddyyyyMatch
    const monthNum = parseInt(month, 10)
    const dayNum = parseInt(day, 10)
    const yearNum = parseInt(year, 10)
    
    // Validate ranges
    if (monthNum < 1 || monthNum > 12) {
      return { valid: false, error: `${fieldName} month must be between 1 and 12` }
    }
    if (dayNum < 1 || dayNum > 31) {
      return { valid: false, error: `${fieldName} day must be between 1 and 31` }
    }
    if (yearNum < 1900 || yearNum > new Date().getFullYear() + 10) {
      return { valid: false, error: `${fieldName} year seems unrealistic (${yearNum})` }
    }
    return { valid: true }
  }
  
  // Fallback to standard Date parsing
  const date = new Date(trimmed)
  if (isNaN(date.getTime())) {
    return { valid: false, error: `${fieldName} must be a valid date (MM-DD-YYYY format preferred)` }
  }
  
  // Check if date is reasonable (not too far in past/future)
  const currentYear = new Date().getFullYear()
  const year = date.getFullYear()
  if (year < 1900 || year > currentYear + 10) {
    return { valid: false, error: `${fieldName} year seems unrealistic (${year})` }
  }
  
  return { valid: true }
}

export const validatePrice = (priceStr: string): ValidationResult => {
  if (!priceStr) return { valid: true } // Price is optional
  
  const trimmed = priceStr.trim()
  if (!trimmed) return { valid: true }
  
  // Remove currency symbols and spaces
  const cleaned = trimmed.replace(/[$£€¥\s,]/g, '')
  
  const price = parseFloat(cleaned)
  if (isNaN(price)) {
    return { valid: false, error: 'Subscription price must be a valid number' }
  }
  
  if (price < 0) {
    return { valid: false, error: 'Subscription price cannot be negative' }
  }
  
  if (price > 10000) {
    return { valid: false, error: 'Subscription price seems too high (over $10,000)' }
  }
  
  return { valid: true }
}

export const validateUUID = (uuidStr: string, fieldName: string): ValidationResult => {
  if (!uuidStr) return { valid: true } // UUID is optional
  
  const trimmed = uuidStr.trim()
  if (!trimmed) return { valid: true }
  
  // UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  if (!uuidRegex.test(trimmed)) {
    return { valid: false, error: `${fieldName} must be a valid UUID format` }
  }
  
  return { valid: true }
}

export const validateInteger = (intStr: string, fieldName: string): ValidationResult => {
  if (!intStr) return { valid: true } // Integer is optional
  
  const trimmed = intStr.trim()
  if (!trimmed) return { valid: true }
  
  const parsed = parseInt(trimmed, 10)
  if (isNaN(parsed)) {
    return { valid: false, error: `${fieldName} must be a valid integer` }
  }
  
  if (parsed < 0) {
    return { valid: false, error: `${fieldName} cannot be negative` }
  }
  
  return { valid: true }
}

export const validateBoolean = (boolStr: string, fieldName: string): ValidationResult => {
  if (!boolStr) return { valid: true } // Boolean is optional
  
  const trimmed = boolStr.trim().toLowerCase()
  if (!trimmed) return { valid: true }
  
  const validValues = ['yes', 'no', 'true', 'false', '1', '0', 'y', 'n']
  if (!validValues.includes(trimmed)) {
    return { valid: false, error: `${fieldName} must be Yes/No, true/false, or 1/0` }
  }
  
  return { valid: true }
}

// Sanitization functions
export const sanitizeString = (str: string): string => {
  if (!str) return ''
  
  return str
    .trim()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/[<>'"&]/g, '') // Remove potentially dangerous characters
}

export const sanitizeEmail = (email: string): string => {
  if (!email) return ''
  return email.trim().toLowerCase()
}

export const sanitizePhoneNumber = (phone: string): string | null => {
  if (!phone) return null
  
  const trimmed = phone.trim()
  if (!trimmed) return null
  
  // Keep common formatting but clean up
  return trimmed.replace(/[^\d\s\-().+]/g, '')
}

export const sanitizePrice = (price: string): string | null => {
  if (!price) return null
  
  const trimmed = price.trim()
  if (!trimmed) return null
  
  // Remove currency symbols and format as number
  const cleaned = trimmed.replace(/[$£€¥\s,]/g, '')
  const parsed = parseFloat(cleaned)
  
  if (isNaN(parsed)) return null
  
  // Format to 2 decimal places
  return parsed.toFixed(2)
}

export const sanitizeDate = (date: string): string | null => {
  if (!date) return null
  
  const trimmed = date.trim()
  if (!trimmed) return null
  
  // Try to parse MM-DD-YYYY format first (US format)
  const mmddyyyyRegex = /^(\d{1,2})-(\d{1,2})-(\d{4})$/
  const mmddyyyyMatch = trimmed.match(mmddyyyyRegex)
  
  if (mmddyyyyMatch) {
    const [, month, day, year] = mmddyyyyMatch
    const monthNum = parseInt(month, 10)
    const dayNum = parseInt(day, 10)
    const yearNum = parseInt(year, 10)
    
    // Validate month and day ranges
    if (monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
      // Format as YYYY-MM-DD for database storage
      return `${yearNum}-${monthNum.toString().padStart(2, '0')}-${dayNum.toString().padStart(2, '0')}`
    }
  }
  
  // Fallback to standard Date parsing (handles YYYY-MM-DD and other formats)
  const parsed = new Date(trimmed)
  if (isNaN(parsed.getTime())) return null
  
  // Format as YYYY-MM-DD
  return parsed.toISOString().split('T')[0]
}

export const sanitizeUUID = (uuid: string): string | null => {
  if (!uuid) return null
  
  const trimmed = uuid.trim()
  if (!trimmed) return null
  
  // Validate and return lowercase UUID
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  if (!uuidRegex.test(trimmed)) return null
  
  return trimmed.toLowerCase()
}

export const sanitizeInteger = (intStr: string): number | null => {
  if (!intStr) return null
  
  const trimmed = intStr.trim()
  if (!trimmed) return null
  
  const parsed = parseInt(trimmed, 10)
  if (isNaN(parsed) || parsed < 0) return null
  
  return parsed
}

export const sanitizeBoolean = (boolStr: string): boolean | null => {
  if (!boolStr) return null
  
  const trimmed = boolStr.trim().toLowerCase()
  if (!trimmed) return null
  
  const trueValues = ['yes', 'true', '1', 'y']
  return trueValues.includes(trimmed)
}

export const sanitizeText = (text: string): string | null => {
  if (!text) return null
  
  const trimmed = text.trim()
  if (!trimmed) return null
  
  return sanitizeString(trimmed)
}

// Normalize column headers to handle common variations
// e.g., "First Name" → "first_name", "Email Address" → "email"
const HEADER_ALIASES: Record<string, string> = {
  // first_name variations
  'first_name': 'first_name',
  'firstname': 'first_name',
  'first name': 'first_name',
  'first': 'first_name',
  'fname': 'first_name',
  'given name': 'first_name',
  'given_name': 'first_name',
  // last_name variations
  'last_name': 'last_name',
  'lastname': 'last_name',
  'last name': 'last_name',
  'last': 'last_name',
  'lname': 'last_name',
  'surname': 'last_name',
  'family name': 'last_name',
  'family_name': 'last_name',
  // email variations
  'email': 'email',
  'email_address': 'email',
  'email address': 'email',
  'emailaddress': 'email',
  'e-mail': 'email',
  'e-mail address': 'email',
  'e_mail': 'email',
  // phone_number variations
  'phone_number': 'phone_number',
  'phone number': 'phone_number',
  'phonenumber': 'phone_number',
  'phone': 'phone_number',
  'mobile': 'phone_number',
  'mobile_number': 'phone_number',
  'mobile number': 'phone_number',
  'cell': 'phone_number',
  'cell_phone': 'phone_number',
  'cell phone': 'phone_number',
  // dob variations
  'dob': 'dob',
  'date_of_birth': 'dob',
  'date of birth': 'dob',
  'dateofbirth': 'dob',
  'birthday': 'dob',
  'birth_date': 'dob',
  'birth date': 'dob',
  'birthdate': 'dob',
}

export const normalizeHeader = (header: string): string => {
  const cleaned = header.trim().toLowerCase().replace(/^["']|["']$/g, '')
  return HEADER_ALIASES[cleaned] || cleaned.replace(/\s+/g, '_')
}

// Helper function to properly parse a CSV line with quoted fields
const parseCSVLine = (line: string): string[] => {
  const result: string[] = []
  let current = ''
  let inQuotes = false
  let i = 0
  
  while (i < line.length) {
    const char = line[i]
    const nextChar = line[i + 1]
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote within quoted field (doubled quote)
        current += '"'
        i += 2
      } else {
        // Toggle quote state
        inQuotes = !inQuotes
        i++
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator outside of quotes
      result.push(current.trim())
      current = ''
      i++
    } else {
      // Regular character
      current += char
      i++
    }
  }
  
  // Add the last field
  result.push(current.trim())
  
  return result
}

// CSV parsing function that properly handles quoted commas
// CSV data is dynamic - return type is any[] since column structure varies
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseCSV = (csvText: string): any[] => {
  const lines = csvText.trim().split(/\r?\n/)
  if (lines.length < 2) return []
  
  // Parse headers using proper CSV parsing, then normalize to handle common variations
  // e.g., "First Name" → "first_name", "Email Address" → "email"
  const rawHeaders = parseCSVLine(lines[0]).map(h => h.replace(/^["']|["']$/g, ''))
  const headers = rawHeaders.map(normalizeHeader)
  const rows = []
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue // Skip empty lines
    
    // Parse values using proper CSV parsing that handles quoted commas
    const values = parseCSVLine(line).map(v => v.replace(/^["']|["']$/g, ''))
    
    if (values.length === 0 || (values.length === 1 && values[0] === '')) {
      continue // Skip empty rows
    }
    
    // Skip status indicator row (second row with "REQUIRED"/"Optional" values)
    // Check if all values are status indicators (REQUIRED, Optional, or empty)
    const isStatusRow = values.every(v => 
      v === 'REQUIRED' || 
      v === 'Required' || 
      v === 'required' || 
      v === 'Optional' || 
      v === 'optional' || 
      v === ''
    )
    if (isStatusRow && values.some(v => v === 'REQUIRED' || v === 'Required' || v === 'required' || v === 'Optional' || v === 'optional')) {
      continue // Skip status row
    }
    
    // CSV row structure is dynamic based on headers
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const row: any = {}
    headers.forEach((header, index) => {
      row[header] = values[index] || ''
    })
    
    rows.push(row)
  }
  
  return rows
}

// Validate a single row
// CSV row structure is dynamic - validated at runtime
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const validateRow = (row: any, rowIndex: number): RowValidationResult => {
  const errors: string[] = []
  const warnings: string[] = []

  // Required fields validation
  const firstNameValidation = validateName(row.first_name, 'First name')
  if (!firstNameValidation.valid) {
    errors.push(`Row ${rowIndex}: ${firstNameValidation.error}`)
  }

  const lastNameValidation = validateName(row.last_name, 'Last name')
  if (!lastNameValidation.valid) {
    errors.push(`Row ${rowIndex}: ${lastNameValidation.error}`)
  }

  // Email validation - required but handled separately for invalid email reporting
  const emailValidation = validateEmail(row.email)
  if (!emailValidation.valid) {
    errors.push(`Row ${rowIndex}: ${emailValidation.error}`)
  }

  // Optional fields validation
  const phoneValidation = validatePhoneNumber(row.phone_number)
  if (!phoneValidation.valid) {
    errors.push(`Row ${rowIndex}: ${phoneValidation.error}`)
  }

  const startDateValidation = validateDate(row.contract_start_date, 'Contract start date')
  if (!startDateValidation.valid) {
    errors.push(`Row ${rowIndex}: ${startDateValidation.error}`)
  }

  const endDateValidation = validateDate(row.contract_end_date, 'Contract end date')
  if (!endDateValidation.valid) {
    errors.push(`Row ${rowIndex}: ${endDateValidation.error}`)
  }

  const originalStartDateValidation = validateDate(row.original_start_date, 'Original start date')
  if (!originalStartDateValidation.valid) {
    errors.push(`Row ${rowIndex}: ${originalStartDateValidation.error}`)
  }

  const priceValidation = validatePrice(row.subscription_price)
  if (!priceValidation.valid) {
    errors.push(`Row ${rowIndex}: ${priceValidation.error}`)
  }

  // Validate UUID fields
  const subscriptionIdValidation = validateUUID(row.subscription_id, 'Subscription ID')
  if (!subscriptionIdValidation.valid) {
    warnings.push(`Row ${rowIndex}: ${subscriptionIdValidation.error}`)
  }

  const discountCodeIdValidation = validateUUID(row.discount_code_id, 'Discount Code ID')
  if (!discountCodeIdValidation.valid) {
    warnings.push(`Row ${rowIndex}: ${discountCodeIdValidation.error}`)
  }

  const locationIdValidation = validateUUID(row.primary_location_id, 'Primary Location ID')
  if (!locationIdValidation.valid) {
    warnings.push(`Row ${rowIndex}: ${locationIdValidation.error}`)
  }

  // Validate integer fields
  const punchCardTotalValidation = validateInteger(row.punch_card_total, 'Punch card total')
  if (!punchCardTotalValidation.valid) {
    errors.push(`Row ${rowIndex}: ${punchCardTotalValidation.error}`)
  }

  // Handle both punch_card_limit and punch_card_remaining (CSV might have either)
  const punchCardLimitValue = row.punch_card_limit || row.punch_card_remaining || ''
  const punchCardLimitValidation = validateInteger(punchCardLimitValue, 'Punch card limit')
  if (!punchCardLimitValidation.valid) {
    errors.push(`Row ${rowIndex}: ${punchCardLimitValidation.error}`)
  }

  // Validate boolean fields
  const trialMemberValidation = validateBoolean(row.is_trial_member, 'Trial Member')
  if (!trialMemberValidation.valid) {
    errors.push(`Row ${rowIndex}: ${trialMemberValidation.error}`)
  }

  const familyLeadValidation = validateBoolean(row.is_family_lead, 'Is Family Lead')
  if (!familyLeadValidation.valid) {
    errors.push(`Row ${rowIndex}: ${familyLeadValidation.error}`)
  }

  // Warnings for missing optional data
  if (!row.phone_number?.trim()) {
    warnings.push(`Row ${rowIndex}: Phone number is missing`)
  }
  if (!row.contract_start_date?.trim()) {
    warnings.push(`Row ${rowIndex}: Contract start date is missing`)
  }
  if (!row.subscription_name?.trim() && !row.subscription_id?.trim()) {
    warnings.push(`Row ${rowIndex}: Subscription name or ID is missing`)
  }

  return { errors, warnings }
}

// Process entire CSV
export const processCSV = (csvText: string): CSVProcessingResult => {
  const errors: string[] = []
  const warnings: string[] = []
  const validRows: ProcessedRow[] = []
  const invalidEmails: InvalidEmailRecord[] = []

  try {
    const rows = parseCSV(csvText)
    
    if (rows.length === 0) {
      return {
        success: false,
        validRows: [],
        errors: ['CSV file is empty or has no valid data rows'],
        warnings: [],
        invalidEmails: [],
        totalRows: 0,
        validRowCount: 0
      }
    }

    // Check for required columns
    const firstRow = rows[0]
    const requiredColumns = ['first_name', 'last_name', 'email']
    const missingColumns = requiredColumns.filter(col => !(col in firstRow))

    if (missingColumns.length > 0) {
      const foundColumns = Object.keys(firstRow).join(', ')
      errors.push(
        `Missing required columns: ${missingColumns.join(', ')}. ` +
        `After normalizing common header variations (e.g. "First Name" → "first_name"), your CSV header row must still include the columns "first_name", "last_name", and "email". ` +
        `Columns detected (after normalization): ${foundColumns || '(none)'}`
      )
    }

    // Validate each row
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      const rowIndex = i + 2 // +2 because CSV row 1 is headers, and we're 0-indexed

      // Check email first - separate invalid emails from other errors
      const emailValidation = validateEmail(row.email)
      const hasInvalidEmail = !emailValidation.valid || !row.email?.trim()
      
      if (hasInvalidEmail) {
        invalidEmails.push({
          row: rowIndex,
          first_name: row.first_name?.trim() || null,
          last_name: row.last_name?.trim() || null,
          email: row.email?.trim() || null,
          reason: emailValidation.error || 'Email is blank or missing'
        })
        // Skip this row - don't add to errors or process further
        continue
      }

      // NOTE: Duplicate detection within CSV is removed - let the backend handle duplicate checking
      // against existing database records. Family members may legitimately share emails, and we
      // can't determine true duplicates without checking against the database.
      const normalizedEmail = sanitizeEmail(row.email)

      // Validate row data (excluding email which we already checked)
      const validation = validateRow(row, rowIndex)
      errors.push(...validation.errors)
      warnings.push(...validation.warnings)

      // If row is valid (no errors), sanitize and add to valid rows
      if (validation.errors.length === 0) {
        // Calculate punch_card_remaining if both total and limit are provided
        // Handle both punch_card_limit and punch_card_remaining (CSV might have either)
        const punchCardTotal = sanitizeInteger(row.punch_card_total)
        const punchCardLimit = sanitizeInteger(row.punch_card_limit || row.punch_card_remaining || '')
        const punchCardRemaining = (punchCardTotal !== null && punchCardLimit !== null) 
          ? Math.max(0, punchCardLimit - punchCardTotal)
          : null

        const processedRow: ProcessedRow = {
          first_name: sanitizeString(row.first_name),
          last_name: sanitizeString(row.last_name),
          email: normalizedEmail,
          phone_number: sanitizePhoneNumber(row.phone_number),
          dob: sanitizeDate(row.dob),
          contract_start_date: sanitizeDate(row.contract_start_date),
          contract_end_date: sanitizeDate(row.contract_end_date),
          subscription_price: sanitizePrice(row.subscription_price),
          subscription_name: sanitizeText(row.subscription_name),
          family_id: sanitizeText(row.family_id),
          is_family_lead: sanitizeBoolean(row.is_family_lead),
          original_start_date: sanitizeDate(row.original_start_date),
          subscription_id: sanitizeUUID(row.subscription_id),
          discount_code_id: sanitizeUUID(row.discount_code_id),
          primary_location_id: sanitizeUUID(row.primary_location_id),
          punch_card_total: punchCardTotal,
          punch_card_limit: punchCardLimit,
          punch_card_remaining: punchCardRemaining,
          is_trial_member: sanitizeBoolean(row.is_trial_member),
          notes: sanitizeText(row.notes)
        }
        
        validRows.push(processedRow)
      }
    }

    return {
      success: errors.length === 0 && invalidEmails.length === 0,
      validRows,
      errors,
      warnings,
      invalidEmails,
      totalRows: rows.length,
      validRowCount: validRows.length
    }

  } catch (error) {
    return {
      success: false,
      validRows: [],
      errors: [`Failed to parse CSV: ${error instanceof Error ? error.message : 'Unknown error'}`],
      warnings: [],
      invalidEmails: [],
      totalRows: 0,
      validRowCount: 0
    }
  }
}